import {
  useEffect,
  useState,
  type ReactNode,
  type PropsWithChildren,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import qs from 'qs';
import type { Position } from 'geojson';
import type { MapViewState } from '@deck.gl/core';
import type { CustomArea } from '../../types/domain';
import { AGE_GROUPS } from '../../constants/ageGroup';
import { GENDERS } from '../../constants/gender';
import { AREA_TYPES } from '../../constants/areaType';
import { LINK_TYPES, type LinkType } from '../../constants/linkType';
import {
  useAggregationConfig,
  AGGREGATION_TARGETS,
  AGGREGATION_SUB_TARGETS,
  AGGREGATION_TYPES,
} from '../../hooks/SimViewer/useAggregationConfig';
import { useMapVisibility } from '../../hooks/SimViewer/useMapVisibility';
import {
  useMapViewState,
  useMapViewStateUpdater,
} from '../../hooks/SimViewer/useMapViewState';
import { useCustomAreas } from '../../hooks/SimViewer/useCustomAreas';
import {
  useDataFilter,
  type AreaFilter,
  type LinkFilter,
} from '../../hooks/SimViewer/useDataFilter';
import { useApiDataFilter } from '../../hooks/SimViewer/useApiDataFilter';
import { unique } from '../../utils/array';
import { filterNullish } from '../../utils/record';
import {
  parseAsNumber,
  parseAsEnum,
  parseAsEnumArray,
  parseAsStringArray,
  parseAsRange,
  parseAsObject,
  parseAsCoordinates,
  parseCustomAreas,
} from '../../utils/query';

type Props = PropsWithChildren<{
  initialViewState: MapViewState;
}>;

// Reactの状態とURLのクエリパラメータを双方向に同期し、URLからの復元が終わってからchildrenを描画するコンポーネント
// フックにしてしまうと呼び出しているコンポーネント自体が再レンダリングされてしまうため、コンポーネントとして実装する
// childrenは親で生成された要素のため、このコンポーネントが再レンダリングされても再レンダリングされない
const StateUrlSynchronizer = ({
  initialViewState,
  children,
}: Props): ReactNode => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { mapViewState } = useMapViewState();
  const { updateMapViewState } = useMapViewStateUpdater();
  const { aggregationConfig, startAggregation } = useAggregationConfig();
  const {
    visibleAreaType,
    visibleLinkTypes,
    isAggregationVisible,
    setVisibleAreaType,
    setVisibleLinkTypes,
    setIsAggregationVisible,
  } = useMapVisibility();
  const { customAreas, setCustomAreas } = useCustomAreas();
  const { setDataFilter } = useDataFilter();
  const { apiFilter } = useApiDataFilter();

  const [isRestored, setIsRestored] = useState<boolean>(false);

  useEffect((): void => {
    const queryParams = qs.parse(search, {
      ignoreQueryPrefix: true,
      arrayLimit: Infinity,
    });

    // カスタムエリアを復元する
    const restoredCustomAreas = parseCustomAreas(queryParams.editable_polygons);
    if (restoredCustomAreas.length > 0) {
      setCustomAreas(restoredCustomAreas);
    }

    // クエリ中のポリゴンの座標を、復元したカスタムエリアの key に対応づける
    const toCustomAreaKeys = (polygons: Position[][]): string[] => {
      return unique(
        polygons.flatMap((coordinates: Position[]): string[] => {
          return restoredCustomAreas
            .filter((customArea: CustomArea): boolean => {
              return (
                JSON.stringify(coordinates) ===
                JSON.stringify(customArea.geometry.coordinates)
              );
            })
            .map((customArea: CustomArea): string => {
              return customArea.key;
            });
        })
      );
    };

    const parseAsAreaFilter = (value: unknown): AreaFilter => {
      const { area, polygon } = parseAsObject(value);
      return {
        areaKeyCodes: unique(parseAsStringArray(area)),
        customAreaKeys: toCustomAreaKeys(parseAsCoordinates(polygon)),
      };
    };

    const parseAsAreaLinkFilter = (value: unknown): AreaFilter & LinkFilter => {
      return {
        ...parseAsAreaFilter(value),
        wayIds: unique(parseAsStringArray(parseAsObject(value).link)),
      };
    };

    // 地図の状態を復元する(クエリパラメータを優先して初期表示状態を上書きする)
    updateMapViewState({
      ...initialViewState,
      ...filterNullish({
        latitude: parseAsNumber(queryParams.lat),
        longitude: parseAsNumber(queryParams.lng),
        zoom: parseAsNumber(queryParams.zoom),
        pitch: parseAsNumber(queryParams.pitch),
        bearing: parseAsNumber(queryParams.bearing),
      }),
    });

    // フィルターの状態を復元する
    setDataFilter({
      start: parseAsAreaFilter(queryParams.start),
      end: parseAsAreaFilter(queryParams.end),
      pass: parseAsAreaLinkFilter(queryParams.pass),
      home: parseAsAreaFilter(queryParams.home),
      work: parseAsAreaFilter(queryParams.work),
      visit: parseAsAreaFilter(queryParams.visit),
      passRelation: parseAsEnum(parseAsObject(queryParams.pass).relation, [
        'and',
        'or',
      ]),
      endActivityTypes: parseAsStringArray(queryParams.end_activity_type),
      longestDistanceModes: parseAsStringArray(
        queryParams.longest_distance_mode
      ),
      genderIds: parseAsEnumArray(queryParams.gender, GENDERS),
      ageGroups: parseAsEnumArray(queryParams.age, AGE_GROUPS),
      nationalities: parseAsStringArray(queryParams.nationality),
      traveledTime: parseAsRange(queryParams.traveled_time),
      traveledDistance: parseAsRange(queryParams.traveled_distance),
      activityTime: parseAsRange(queryParams.activity_time),
      depTime: parseAsRange(queryParams.dep_time),
      arrTime: parseAsRange(queryParams.arr_time),
      passageTime: parseAsRange(queryParams.passage_time),
      stayingTime: parseAsRange(queryParams.staying_time),
      stopBy: parseAsEnum(queryParams.stopBy, ['before', 'after', 'all']),
    });

    // 地図の可視化状態を復元する
    const target = parseAsEnum(queryParams.target, AGGREGATION_TARGETS);
    const subTarget = parseAsEnum(
      queryParams.subTarget,
      AGGREGATION_SUB_TARGETS
    );
    const type = parseAsEnum(queryParams.type, AGGREGATION_TYPES);
    if (target) {
      startAggregation(target, subTarget ?? undefined, type ?? undefined);
    }

    // エリアの可視化状態を復元する
    const areaType = parseAsEnum(queryParams.area_type, [
      '',
      ...AREA_TYPES,
    ] as const);
    if (areaType === '') {
      setVisibleAreaType(null);
    } else {
      setVisibleAreaType(areaType || 'town');
    }

    // リンクの可視化状態を復元する
    const linkTypes = parseAsStringArray(queryParams.link_type).filter(
      (linkType: string): linkType is LinkType => {
        return parseAsEnum(linkType, LINK_TYPES) !== null;
      }
    );
    setVisibleLinkTypes(linkTypes);

    // 分析結果の可視化状態を復元する
    const aggregation = parseAsEnum(queryParams.aggregation, [
      'true',
      'false',
    ] as const);
    if (aggregation) {
      setIsAggregationVisible(aggregation === 'true');
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect((): void => {
    // Reactの状態が変わる度にURLのクエリパラメータを同期する
    // ただし復元前の状態でURLを上書きしないよう、復元が終わってから同期を始める
    if (!isRestored) {
      return;
    }

    const queryString = [
      qs.stringify(apiFilter),
      qs.stringify(aggregationConfig),
      qs.stringify({
        area_type: visibleAreaType,
        link_type: visibleLinkTypes,
        aggregation: isAggregationVisible,
      }),
      qs.stringify({
        editable_polygons: customAreas,
      }),
      qs.stringify({
        lat: mapViewState.latitude.toFixed(6),
        lng: mapViewState.longitude.toFixed(6),
        zoom: mapViewState.zoom.toFixed(2),
        pitch: (mapViewState.pitch ?? 0).toFixed(2),
        bearing: (mapViewState.bearing ?? 0).toFixed(2),
      }),
    ]
      .filter(Boolean)
      .join('&');

    // ページ遷移を伴わずにURLのクエリパラメータを更新する
    navigate(`?${queryString}`, { replace: true });
  }, [
    isRestored,
    navigate,
    customAreas,
    apiFilter,
    aggregationConfig,
    visibleAreaType,
    visibleLinkTypes,
    isAggregationVisible,
    mapViewState,
  ]);

  // 意図せぬ状態の上書きや画面のチラつきを防ぐため、URLからの状態復元が完了してから子コンポーネントを描画する
  return isRestored ? children : null;
};

export { StateUrlSynchronizer };
